<template>
    <div class="page__content kb-editor" v-if="article">
        <div class="columns">
            <div class="column is-9">
                <kb-back :to="{name: 'kb-topic', params: {lang, topicId: article.topic}}">
                    {{ $t('knowledgebase.backToTopic') }}
                </kb-back>
                <kb-inline-title
                    :value="article.title || ''"
                    :placeholder="$t('knowledgebase.untitledArticle')"
                    :has-draft="article.has_draft"
                    :can-revert="article.has_draft && article.title_published != null"
                    :disabled="article.will_be_deleted"
                    @save="saveTitle"
                    @revert="revertTitle"
                />
                <p class="kb-editor__meta">
                    {{ $t('knowledgebase.updatedPrefix') }} <kb-time :value="article.updated_at" />
                </p>
            </div>
            <div class="column is-3 has-text-right">
                <button
                    v-if="article.will_be_deleted"
                    class="button is-warning is-outlined"
                    @click="cancelDeletion"
                >
                    <span class="icon"><i class="fa fa-undo" /></span>
                    <span>{{ $t('knowledgebase.cancelDeletion') }}</span>
                </button>
                <button
                    v-else
                    class="button is-danger is-outlined"
                    @click="confirmDelete"
                >
                    <span class="icon"><i class="fa fa-trash" /></span>
                    <span>
                        {{ article.title_published != null
                            ? $t('knowledgebase.stageDeletion')
                            : $t('knowledgebase.deleteArticle') }}
                    </span>
                </button>
            </div>
        </div>

        <div
            v-if="article.will_be_deleted"
            class="notification is-warning kb-editor__deletion-banner"
        >
            {{ $t('knowledgebase.articlePendingDeletion') }}
        </div>

        <!-- Two tabs because Content and Visibility are different kinds of
             concerns and burying Visibility at the bottom of the scroll made
             it undiscoverable. Tab state is local — no router involvement; an
             article reload preserves the user's current tab. -->
        <div class="kb-editor__tabs">
            <button
                class="kb-editor__tab"
                :class="{'kb-editor__tab--active': activeTab === 'content'}"
                @click="activeTab = 'content'"
            >
                {{ $t('knowledgebase.tabContent') }}
                <span class="kb-editor__tab-badge" v-if="contentBadgeCount > 0">
                    {{ contentBadgeCount }}
                </span>
            </button>
            <button
                class="kb-editor__tab"
                :class="{'kb-editor__tab--active': activeTab === 'visibility'}"
                @click="activeTab = 'visibility'"
            >
                {{ $t('knowledgebase.tabVisibility') }}
                <span class="kb-editor__tab-badge" v-if="visibilityBadgeCount > 0">
                    {{ visibilityBadgeCount }}
                </span>
            </button>
        </div>

        <kb-article-content
            v-if="activeTab === 'content'"
            :article="article"
            :seasons="seasons"
            :seasons-loaded="seasonsLoaded"
            :lang="lang"
            @changed="load"
        />
        <kb-visibility-section
            v-else-if="activeTab === 'visibility'"
            :article="article"
            :connections="connections"
            :disabled="article.will_be_deleted"
            @updated="onArticleUpdated"
        />

        <p class="kb-editor__saving" v-if="saving">{{ $t('knowledgebase.saving') }}</p>

        <kb-prompt-modal ref="prompt" />
    </div>
</template>

<script>
import { Api } from '../../../api';
import { AppModel } from '../../../models';
import { EventBus } from '../../../utils';
import KbArticleContent from './kb-article-content.vue';
import KbBack from './kb-back.vue';
import KbInlineTitle from './kb-inline-title.vue';
import KbPromptModal from './kb-prompt-modal.vue';
import KbTime from './kb-time.vue';
import KbVisibilitySection from './kb-visibility-section.vue';

// Shell. Owns the article + seasons + connections fetch, the title bar, the
// delete/cancel-deletion button, and the tab switcher. Routes the active
// tab to the right child component. Each child emits `@changed` (Content)
// or `@updated` (Visibility) to ask the shell to refresh the article.
//
// Variant editing lives in kb-article-content.vue; per-channel visibility
// in kb-visibility-section.vue.

export default {
    name: 'kb-article-editor',

    components: {
        KbArticleContent,
        KbBack,
        KbInlineTitle,
        KbPromptModal,
        KbTime,
        KbVisibilitySection,
    },

    props: ['lang'],

    data() {
        return {
            article: null,
            seasons: [],
            seasonsLoaded: false,
            connections: [],
            saving: false,
            activeTab: 'content',
        };
    },

    computed: {
        articleId() {
            return Number(this.$route.params.articleId);
        },
        contentBadgeCount() {
            // Number of pending content-side changes (title draft, variant
            // body drafts, variant deletions). Mirrors what the publish modal
            // would show for this article on the Content side.
            if(!this.article) return 0;
            let n = 0;
            if(this.article.title_draft != null && !this.article.will_be_deleted) n++;
            for(const v of (this.article.variants || [])) {
                if(v.body_draft != null && !v.will_be_deleted) n++;
                if(v.is_pending_deletion) n++;
            }
            return n;
        },
        visibilityBadgeCount() {
            // Number of pending exclusion changes on this article.
            if(!this.article) return 0;
            let n = 0;
            for(const ex of (this.article.channel_exclusions || [])) {
                if(ex.state === 'staged_add' || ex.state === 'staged_remove') n++;
            }
            return n;
        },
    },

    methods: {
        async load() {
            const articleRes = await Api.knowledgebase.fetchArticle(this.articleId);
            this.article = articleRes.body;

            // Seasons and connections are independent of each other; fetch
            // both lazily and survive either failing — the editor's still
            // useful with the default body and no channel toggles.
            try {
                const seasonsRes = await Api.seasons.fetchSeasons();
                const body = seasonsRes.body;
                const list = body && body.results ? body.results : (body || []);
                this.seasons = list.filter(s => !s.is_deleted);
            } catch (e) {
                this.seasons = [];
            } finally {
                this.seasonsLoaded = true;
            }

            try {
                const connsRes = await Api.knowledgebase.fetchConnections();
                const body = connsRes.body;
                this.connections = body && body.results ? body.results : (body || []);
            } catch (e) {
                this.connections = [];
            }
        },
        onArticleUpdated(article) {
            // Visibility toggle returns the refreshed article — swap in
            // place rather than a full refetch.
            this.article = article;
        },
        async saveTitle(next) {
            this.saving = true;
            try {
                await Api.knowledgebase.updateArticle(this.article.id, {
                    title_draft: next,
                });
                await this.load();
            } finally {
                this.saving = false;
            }
        },
        async revertTitle() {
            this.saving = true;
            try {
                await Api.knowledgebase.discardArticleDraft(this.article.id);
                await this.load();
            } finally {
                this.saving = false;
            }
        },
        async confirmDelete() {
            const isLive = this.article.title_published != null;
            const message = isLive
                ? this.$t('knowledgebase.confirmDeleteArticleLive')
                : this.$t('knowledgebase.confirmDeleteArticleDraftOnly');

            const ok = await this.$refs.prompt.confirm({
                title: this.$t('knowledgebase.deleteArticle'),
                message,
                submitText: isLive
                    ? this.$t('knowledgebase.stageDeletion')
                    : this.$t('knowledgebase.delete'),
                danger: true,
            });
            if(!ok) return;
            const topicId = this.article.topic;
            await Api.knowledgebase.deleteArticle(this.article.id);
            if(isLive) {
                await this.load();
            } else {
                this.$router.push({
                    name: 'kb-topic',
                    params: {lang: this.lang, topicId},
                });
            }
        },
        async cancelDeletion() {
            await Api.knowledgebase.restoreArticle(this.article.id);
            await this.load();
        },
        _onBootstrap() {
            this.load();
        },
    },

    created() {
        if(AppModel.activeCampground) {
            this.load();
        }
        EventBus.$on(EventBus.BOOTSTRAP_DONE, this._onBootstrap);
    },

    beforeDestroy() {
        EventBus.$off(EventBus.BOOTSTRAP_DONE, this._onBootstrap);
    },

    watch: {
        '$route.params.articleId'() {
            if(AppModel.activeCampground) this.load();
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../../variables';

.kb-editor__deletion-banner {
    margin-bottom: 16px;
}

.kb-editor__meta {
    font-size: 0.9rem;
    color: #666;
    margin-top: 6px;
}

.kb-editor__saving {
    font-size: 0.85rem;
    color: #888;
    margin-top: 8px;
}

.kb-editor__tabs {
    display: flex;
    gap: 4px;
    margin: 18px 0 14px;
    border-bottom: 1.5px solid #e0e0e0;
}

.kb-editor__tab {
    appearance: none;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    margin-bottom: -1.5px;
    padding: 10px 18px;
    font-size: 0.95rem;
    font-weight: 600;
    color: #666;
    cursor: pointer;
    transition: color 0.15s, border-color 0.15s;
    display: flex;
    align-items: center;
    gap: 6px;

    &:hover {
        color: #222;
    }

    &--active {
        color: $mc-primary;
        border-bottom-color: $mc-primary;
    }
}

.kb-editor__tab-badge {
    background: #f5a623;
    color: white;
    font-size: 0.7rem;
    font-weight: 700;
    padding: 1px 7px;
    border-radius: 999px;
    line-height: 1.4;
}
</style>



// WEBPACK FOOTER //
// src/views/pages/knowledge-base/kb-article-editor.vue